import { useMemo } from 'react'
import {
  AutocompleteRenderGetTagProps,
  Chip,
  ChipProps,
  styled,
} from '@mui/material'
import {
  CellDef,
  WorkloadCellDef,
  DateTermCellDef,
  DateTimeCellDef,
  EntitySearchCellDef,
  MultiEntitySearchCellDef,
  SearchOnEntitySearchCell,
  SelectCellDef,
  TextCellDef,
  NumberCellDef,
} from '../../../../../../containers/SingleSheetV2/table/model'
import { intl } from '../../../../../../../i18n'
import {
  toLabel,
  WbsItemStatus,
} from '../../../../../../../domain/entity/WbsItemEntity'
import { WbsStatusLabel } from '../../../../../../components/data-display/WbsStatusLabel'
import { SelectOption } from '../../../../../../model/selectOption'
import {
  ReferencedEntity,
  ReferencedEntityWithBackgroundColor,
} from '../../../../../../../domain/value-object/ReferencedEntity'
import { getTextColorOnBackgroundColor } from '../../../../../../../utils/color'
import { TagCellEditorRenderOption } from '../../../../../../containers/BulkSheetView/components/cellEditor/TagCellEditor'

export const useCode = (): TextCellDef => {
  const cellDef = useMemo<TextCellDef>(() => {
    return {
      externalId: 'code',
      field: 'code',
      label: intl.formatMessage({ id: 'wbsItem.code' }),
      type: 'TEXT',
      width: 'half',
      required: true,
      editable: false,
    }
  }, [])
  return cellDef
}

export const useDisplayName = (): CellDef => {
  const cellDef = useMemo<CellDef>(() => {
    return {
      externalId: 'displayName',
      field: 'displayName',
      label: intl.formatMessage({ id: 'wbsItem.displayName' }),
      type: 'TEXT',
      width: 'full',
      required: true,
      editable: true,
    }
  }, [])
  return cellDef
}

export const useStatus = (): SelectCellDef => {
  const options = useMemo(
    () =>
      Object.values(WbsItemStatus).map(v => ({
        value: v,
        name: toLabel(v),
      })),
    []
  )
  const cellDef = useMemo<SelectCellDef>(() => {
    return {
      externalId: 'status',
      field: 'status',
      label: intl.formatMessage({ id: 'wbsItem.status' }),
      type: 'SELECT',
      width: 'half',
      required: true,
      editable: true,
      options,
      renderOption: (option: SelectOption) => (
        // TODO Remove cast.
        <WbsStatusLabel status={option.value as WbsItemStatus} />
      ),
      renderValue: (option: SelectOption | undefined) => {
        if (!option) return <></>
        // TODO Remove cast.
        return <WbsStatusLabel status={option.value as WbsItemStatus} />
      },
    }
  }, [options])
  return cellDef
}

export const useSubStatus = (subStatuses: SelectOption[]): SelectCellDef => {
  const cellDef = useMemo<SelectCellDef>(() => {
    return {
      externalId: 'substatus',
      field: 'substatus',
      label: intl.formatMessage({ id: 'wbsItem.substatus' }),
      type: 'SELECT',
      width: 'half',
      required: false,
      editable: true,
      options: subStatuses,
    }
  }, [subStatuses])
  return cellDef
}

/**
 * @deprecated
 * This property is not shown.
 * Remove this property after replacing SingleSheetV2 completely.
 * To make the form layout(row index) same as old SingleSheet(function_properties).
 * In function_properties "hidden" of this property is true.
 */
export const useType = (): TextCellDef => {
  const cellDef = useMemo<TextCellDef>(() => {
    return {
      externalId: 'type',
      field: 'type',
      label: intl.formatMessage({ id: 'wbsItem.type' }),
      type: 'TEXT',
      width: 'half',
      required: false,
      editable: false,
      hidden: true,
    }
  }, [])
  return cellDef
}

export const useTags = (
  searchTag: SearchOnEntitySearchCell
): MultiEntitySearchCellDef => {
  const cellDef = useMemo<MultiEntitySearchCellDef>(() => {
    return {
      externalId: 'tags',
      field: 'tags',
      label: intl.formatMessage({ id: 'wbsItem.tags' }),
      type: 'MULTI_ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchTag,
      displayIcon: false,
      renderOptionRaw: TagCellEditorRenderOption, // TODO Remove
      renderTags: (
        value: ReferencedEntity[],
        getTagProps: AutocompleteRenderGetTagProps
      ) =>
        value.map((v, index) => (
          <StyledChip
            label={v.name}
            backgroundColor={
              (v as ReferencedEntityWithBackgroundColor).backgroundColor
            }
            {...getTagProps({ index })}
            key={getTagProps({ index }).key}
          />
        )),
    }
  }, [searchTag])
  return cellDef
}

export const useTeam = (
  searchTeam: SearchOnEntitySearchCell
): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'team',
      field: 'team',
      label: intl.formatMessage({ id: 'wbsItem.team' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchTeam,
      displayIcon: true,
    }
  }, [searchTeam])
  return cellDef
}

export const useAccountable = (
  searchProjectMember: SearchOnEntitySearchCell
): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'accountable',
      field: 'accountable',
      label: intl.formatMessage({ id: 'wbsItem.accountable' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchProjectMember,
      displayIcon: true,
    }
  }, [searchProjectMember])
  return cellDef
}
export const useResponsible = (
  searchProjectMember: SearchOnEntitySearchCell
): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'responsible',
      field: 'responsible',
      label: intl.formatMessage({ id: 'wbsItem.responsible' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchProjectMember,
      displayIcon: true,
    }
  }, [searchProjectMember])
  return cellDef
}
export const useAssignee = (
  searchProjectMember: SearchOnEntitySearchCell
): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'assignee',
      field: 'assignee',
      label: intl.formatMessage({ id: 'wbsItem.assignee' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchProjectMember,
      displayIcon: true,
    }
  }, [searchProjectMember])
  return cellDef
}
export const useWatchers = (
  searchProjectMember: SearchOnEntitySearchCell
): MultiEntitySearchCellDef => {
  const cellDef = useMemo<MultiEntitySearchCellDef>(() => {
    return {
      externalId: 'watchers',
      field: 'watchers',
      label: intl.formatMessage({ id: 'wbsItem.watchers' }),
      type: 'MULTI_ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchProjectMember,
      displayIcon: true,
    }
  }, [searchProjectMember])
  return cellDef
}

export const useDifficulty = (difficulties: SelectOption[]): SelectCellDef => {
  const cellDef = useMemo<SelectCellDef>(() => {
    return {
      externalId: 'difficulty',
      field: 'difficulty',
      label: intl.formatMessage({ id: 'wbsItem.difficulty' }),
      type: 'SELECT',
      width: 'half',
      required: false,
      editable: true,
      options: difficulties,
    }
  }, [difficulties])
  return cellDef
}

export const usePriority = (priorities: SelectOption[]): SelectCellDef => {
  const cellDef = useMemo<SelectCellDef>(() => {
    return {
      externalId: 'priority',
      field: 'priority',
      label: intl.formatMessage({ id: 'wbsItem.priority' }),
      type: 'SELECT',
      width: 'half',
      required: false,
      editable: true,
      options: priorities,
      displayIcon: true,
    }
  }, [priorities])
  return cellDef
}
export const useEstimatedStoryPoint = (): NumberCellDef => {
  const cellDef = useMemo<NumberCellDef>(() => {
    return {
      externalId: 'estimatedStoryPoint',
      field: 'estimatedStoryPoint',
      label: intl.formatMessage({ id: 'wbsItem.estimatedStoryPoint' }),
      type: 'NUMBER',
      width: 'half',
      required: false,
      editable: true,
      hidden: true,
    }
  }, [])
  return cellDef
}
export const useEstimatedHour = (): WorkloadCellDef => {
  const cellDef = useMemo<WorkloadCellDef>(() => {
    return {
      externalId: 'estimatedHour',
      field: 'estimatedHour',
      label: intl.formatMessage({ id: 'wbsItem.estimatedHour' }),
      type: 'WORKLOAD',
      width: 'half',
      required: false,
      editable: true,
    }
  }, [])
  return cellDef
}
export const useSprint = (
  searchSprint: SearchOnEntitySearchCell
): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'sprint',
      field: 'sprint',
      label: intl.formatMessage({ id: 'wbsItem.sprint' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: true,
      search: searchSprint,
      displayIcon: true,
    }
  }, [searchSprint])
  return cellDef
}
export const useScheduledDate = (): DateTermCellDef => {
  const cellDef = useMemo<DateTermCellDef>(() => {
    return {
      externalId: 'scheduledDate',
      field: 'scheduledDate',
      label: intl.formatMessage({ id: 'wbsItem.scheduledDate' }),
      type: 'DATE_TERM',
      width: 'half',
      required: false,
      editable: true,
    }
  }, [])
  return cellDef
}
export const useActualDate = (): DateTermCellDef => {
  const cellDef = useMemo<DateTermCellDef>(() => {
    return {
      externalId: 'actualDate',
      field: 'actualDate',
      label: intl.formatMessage({ id: 'wbsItem.actualDate' }),
      type: 'DATE_TERM',
      width: 'half',
      required: false,
      editable: true,
    }
  }, [])
  return cellDef
}

export const useCreatedBy = (): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'createdBy',
      field: 'createdBy',
      label: intl.formatMessage({ id: 'wbsItem.createdBy' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: false,
      search: async () => {
        return []
      },
      displayIcon: true,
    }
  }, [])
  return cellDef
}
export const useCreatedAt = (): DateTimeCellDef => {
  const cellDef = useMemo<DateTimeCellDef>(() => {
    return {
      externalId: 'createdAt',
      field: 'createdAt',
      label: intl.formatMessage({ id: 'wbsItem.createdAt' }),
      type: 'DATE_TIME',
      width: 'half',
      required: false,
      editable: false,
    }
  }, [])
  return cellDef
}
export const useUpdatedBy = (): EntitySearchCellDef => {
  const cellDef = useMemo<EntitySearchCellDef>(() => {
    return {
      externalId: 'updatedBy',
      field: 'updatedBy',
      label: intl.formatMessage({ id: 'wbsItem.updatedBy' }),
      type: 'ENTITY_SEARCH',
      width: 'half',
      required: false,
      editable: false,
      search: async () => {
        return []
      },
      displayIcon: true,
    }
  }, [])
  return cellDef
}
export const useUpdatedAt = (): DateTimeCellDef => {
  const cellDef = useMemo<DateTimeCellDef>(() => {
    return {
      externalId: 'updatedAt',
      field: 'updatedAt',
      label: intl.formatMessage({ id: 'wbsItem.updatedAt' }),
      type: 'DATE_TIME',
      width: 'half',
      required: false,
      editable: false,
    }
  }, [])
  return cellDef
}
export const useRevision = (): TextCellDef => {
  const cellDef = useMemo<TextCellDef>(() => {
    return {
      externalId: 'revision',
      field: 'revision',
      label: intl.formatMessage({ id: 'wbsItem.revision' }),
      type: 'TEXT',
      width: 'half',
      required: false,
      editable: false,
    }
  }, [])
  return cellDef
}

const StyledChip = styled((props: ChipProps) => (
  <Chip {...props} size="small" />
))(({ backgroundColor }: { backgroundColor: string }) => {
  const color = useMemo(
    () => getTextColorOnBackgroundColor(backgroundColor),
    [backgroundColor]
  )
  return {
    color,
    backgroundColor,
  }
})
